// Display side of the lead arrival source (see `lead-arrival-source.ts`): the one channel label every
// surface shows (Leads row + drawer, CSV, appointment pill, analytics), the ≤9 groups the source filter and
// the "Top source" KPI count by, and the credit rollup (who earned the lead: ads, social team, Bio/Google).
// ---------------------------------------------------------------------------

import type { LinkBioTrafficSource } from './link-bio-insights';
import type { LeadArrivalVia, LeadMetaPlacement } from './lead-arrival-source';

export const LEAD_META_PLACEMENT_LABELS: Record<LeadMetaPlacement, string> = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  MESSENGER: 'Messenger',
  AUDIENCE_NETWORK: 'Audience Network',
};

/**
 * The one wording of a source, everywhere: Leads, the offer's Guests + Overview, Bio Page analytics, Inbox, CSVs.
 * Never add a second map: a Bio visitor and a lead from the same tap must read the same.
 */
export const LEAD_SOURCE_LABELS: Record<LinkBioTrafficSource, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  GOOGLE_SEARCH: 'Google Search',
  GOOGLE_BUSINESS: 'Google Business',
  GOOGLE_ADS: 'Google Ads',
  META_ADS: 'Meta ads',
  META_OTHER: 'Facebook / Instagram (untagged)',
  QR_CODE: 'QR code',
  OTHER: 'Other website / link',
  DIRECT: 'Direct / shared link',
};

/** The Hayven touchpoint a sign-up came through (CSV "Via" column). */
export const LEAD_ARRIVAL_VIA_LABELS: Record<LeadArrivalVia, string> = {
  COMMENT_DM: 'Comment→DM',
  TEXT_KEYWORD: 'Text keyword',
  BIO_PAGE: 'Bio Page',
  PAGE: 'Sign-up page',
};

/** Shown with a Comment → DM automation whose name is gone (deleted automation). */
const COMMENT_DM_LABEL = 'Comment→DM';
const BIO_PAGE_LABEL = 'Bio Page';
/** A texted keyword names the keyword: "Text · VIP". */
const TEXT_KEYWORD_LABEL = 'Text';

export interface LeadChannelLabelInput {
  via: LeadArrivalVia;
  /** Null only for a row written before sources were stored and not backfilled yet (reads as Direct). */
  source: LinkBioTrafficSource | null;
  /** OTHER source's raw value; for a texted keyword, the keyword as the salon typed it ("VIP"). */
  tag?: string | null;
  metaPlacement?: LeadMetaPlacement | null;
  /** The Comment → DM automation's name, as the owner typed it. */
  commentDmName?: string | null;
}

/**
 * "Meta ads · Instagram", "Comment→DM · “me” on Oct 3", "Text · VIP", "Bio Page ← Instagram", "Direct / shared link".
 * Comment → DM never repeats the platform (the row icon shows it); a texted keyword names the keyword, never
 * "Other website / link"; a Bio Page arrival names its upstream and never reads "Bio Page · Other".
 */
export function leadChannelLabel(input: LeadChannelLabelInput): string {
  const source = input.source ?? 'DIRECT';
  if (input.via === 'COMMENT_DM') {
    const name = input.commentDmName?.trim();
    return name ? `${COMMENT_DM_LABEL} · ${name}` : COMMENT_DM_LABEL;
  }
  if (input.via === 'TEXT_KEYWORD') {
    const keyword = input.tag?.trim();
    return keyword ? `${TEXT_KEYWORD_LABEL} · ${keyword}` : LEAD_ARRIVAL_VIA_LABELS.TEXT_KEYWORD;
  }
  if (input.via === 'BIO_PAGE') {
    if (source === 'DIRECT') return BIO_PAGE_LABEL;
    return `${BIO_PAGE_LABEL} ← ${leadBioUpstreamLabel(source, input.tag)}`;
  }
  if (source === 'META_ADS' && input.metaPlacement) {
    return `${LEAD_SOURCE_LABELS.META_ADS} · ${LEAD_META_PLACEMENT_LABELS[input.metaPlacement]}`;
  }
  if (source === 'OTHER' && input.tag) return `${LEAD_SOURCE_LABELS.OTHER} · ${input.tag}`;
  return LEAD_SOURCE_LABELS[source];
}

/** The channel that sent a guest to the Bio Page, as the "Bio Page ← …" label words it. */
export function leadBioUpstreamLabel(source: LinkBioTrafficSource, tag: string | null | undefined): string {
  if (source === 'META_OTHER') return 'Facebook / Instagram';
  if (source === 'OTHER') return tag || 'another website';
  return LEAD_SOURCE_LABELS[source];
}

/** Source groups for the Leads filter and the "Top source" KPI, in display order. */
export const LEAD_SOURCE_GROUPS = [
  { key: 'META_ADS', label: 'Meta ads' },
  { key: 'GOOGLE_ADS', label: 'Google Ads' },
  { key: 'COMMENT_DM', label: 'Comment→DM' },
  { key: 'INSTAGRAM', label: 'Instagram' },
  { key: 'FACEBOOK', label: 'Facebook' },
  { key: 'BIO_PAGE', label: 'Bio Page' },
  { key: 'GOOGLE', label: 'Google' },
  { key: 'QR_CODE', label: 'QR code' },
  { key: 'DIRECT_OTHER', label: 'Direct / other' },
] as const;
export type LeadSourceGroup = (typeof LEAD_SOURCE_GROUPS)[number]['key'];
/** Non-empty tuple so it can back an enum schema (`z.enum`). */
export const LEAD_SOURCE_GROUP_KEYS = LEAD_SOURCE_GROUPS.map((group) => group.key) as unknown as readonly [
  LeadSourceGroup,
  ...LeadSourceGroup[],
];
export const LEAD_SOURCE_GROUP_LABELS = Object.fromEntries(
  LEAD_SOURCE_GROUPS.map((group) => [group.key, group.label])
) as Record<LeadSourceGroup, string>;

/**
 * Paid ad sources. A sign-up from one of these is an "ad sign-up": its bookings' revenue is the numerator of
 * every return-on-ad-spend figure (the API's ad-sourced revenue predicate mirrors this list), and it counts in
 * the "Paid ads" credit bucket. An untagged Meta click is never paid (it may be an organic post).
 */
export const AD_LEAD_SOURCES = ['META_ADS', 'GOOGLE_ADS'] as const satisfies readonly LinkBioTrafficSource[];
export type AdLeadSource = (typeof AD_LEAD_SOURCES)[number];
const PAID_SOURCES: ReadonlySet<LinkBioTrafficSource> = new Set(AD_LEAD_SOURCES);

/** True for a sign-up that came from a tagged paid ad (Meta ads, Google Ads). */
export function isAdLeadSource(source: LinkBioTrafficSource | null | undefined): source is AdLeadSource {
  return source != null && PAID_SOURCES.has(source);
}

/**
 * The one group a sign-up counts in. Paid wins over via (an ad that sent the guest through the Bio Page is
 * still the ad's), then Comment → DM, then the Bio Page, then the page's own source.
 */
export function leadSourceGroupOf(input: { via: LeadArrivalVia; source: LinkBioTrafficSource | null }): LeadSourceGroup {
  const { source } = input;
  if (isAdLeadSource(source)) return source;
  if (input.via === 'COMMENT_DM') return 'COMMENT_DM';
  if (input.via === 'BIO_PAGE') return 'BIO_PAGE';
  switch (source) {
    case 'INSTAGRAM':
    case 'FACEBOOK':
    case 'QR_CODE':
      return source;
    case 'GOOGLE_SEARCH':
    case 'GOOGLE_BUSINESS':
      return 'GOOGLE';
    default:
      // TikTok, an untagged Meta click, other websites, Direct and not-yet-classified rows.
      return 'DIRECT_OTHER';
  }
}

/** Who earned the lead: the ads budget, the social team (DMs + organic posts), the Bio Page / Google, or other. */
export const LEAD_CREDIT_BUCKETS = ['PAID', 'SOCIAL', 'BIO_GOOGLE', 'OTHER'] as const;
export type LeadCreditBucket = (typeof LEAD_CREDIT_BUCKETS)[number];
export const LEAD_CREDIT_BUCKET_LABELS: Record<LeadCreditBucket, string> = {
  PAID: 'Paid ads',
  SOCIAL: 'Social team',
  BIO_GOOGLE: 'Bio/Google',
  OTHER: 'Other',
};

/**
 * Credit bucket of a sign-up. Paid wins over via, the same rule as `leadSourceGroupOf`. A texted keyword is the
 * social team's: the keyword is said in the agency's videos and posts.
 */
export function leadCreditBucketOf(input: { via: LeadArrivalVia; source: LinkBioTrafficSource | null }): LeadCreditBucket {
  const { source } = input;
  if (isAdLeadSource(source)) return 'PAID';
  if (input.via === 'COMMENT_DM' || input.via === 'TEXT_KEYWORD') return 'SOCIAL';
  if (input.via === 'BIO_PAGE') return 'BIO_GOOGLE';
  switch (source) {
    case 'INSTAGRAM':
    case 'FACEBOOK':
    case 'TIKTOK':
      return 'SOCIAL';
    case 'GOOGLE_SEARCH':
    case 'GOOGLE_BUSINESS':
      return 'BIO_GOOGLE';
    default:
      // QR, other websites, Direct, and an untagged Meta click (organic post or untagged ad: can't tell).
      return 'OTHER';
  }
}
